import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  preloadBorrowerInfo,
  getFullBorrowerInfoSync,
} from "../utils/loanUtils";

const LoanTable = ({
  loanRecords,
  currentPage,
  totalPages,
  onPageChange,
  rowsPerPage = 14,
  isBackCard = false,
}) => {
  // Use consistent row heights for both front and back cards (for back-to-back printing)
  const STYLES = {
    tableContainer: {
      flex: 1,
      minHeight: 0,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "10px",
      tableLayout: "fixed",
    },
    headerCell: {
      border: "1px solid #333",
      padding: "3px 2px",
      textAlign: "center",
      fontSize: "12px",
      fontWeight: "bold",
      backgroundColor: "#f5f5f5",
      height: "20px",
      overflow: "hidden",
      wordWrap: "break-word",
    },
    dataCell: {
      border: "1px solid #333",
      padding: "1px 2px",
      fontSize: "12px",
      textAlign: "center",
      height: "26px",
      overflow: "hidden",
      wordWrap: "break-word",
      lineHeight: "1.3",
    },
    emptyCell: {
      border: "1px solid #333",
      padding: "1px 2px",
      height: "26px",
    },
    loadingCell: {
      border: "1px solid #333",
      padding: "1px 2px",
      fontSize: "12px",
      textAlign: "center",
      height: "26px",
      fontStyle: "italic",
      color: "#666",
      lineHeight: "1.3",
    },
  };

  const [enrichedRecords, setEnrichedRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const safeEnrichedRecords = Array.isArray(enrichedRecords)
    ? enrichedRecords
    : [];

  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentPageRecords = safeEnrichedRecords.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  useEffect(() => {
    const enrichLoanRecords = async () => {
      setIsLoading(true);

      let resolvedRecords = loanRecords;
      if (loanRecords && typeof loanRecords.then === "function") {
        try {
          resolvedRecords = await loanRecords;
        } catch (error) {
          setEnrichedRecords([]);
          setIsLoading(false);
          return;
        }
      }

      let recordsArray = [];
      if (Array.isArray(resolvedRecords)) {
        recordsArray = resolvedRecords;
      } else if (resolvedRecords && typeof resolvedRecords === "object") {
        recordsArray =
          resolvedRecords.hits ||
          resolvedRecords.records ||
          resolvedRecords.data ||
          [];
      } else {
        console.warn(
          "resolvedRecords is not an array or object:",
          resolvedRecords
        );
      }

      if (!recordsArray || recordsArray.length === 0) {
        setEnrichedRecords([]);
        setIsLoading(false);
        return;
      }

      try {
        let enriched = [];

        const hasExistingBorrowerInfo = recordsArray.some(
          (record) =>
            record.borrowerName &&
            record.borrowerName !== "Unknown Borrower" &&
            record.borrowerName.trim() !== ""
        );

        if (hasExistingBorrowerInfo) {
          enriched = recordsArray.map((record) => {
            return {
              ...record,
              borrowerName: record.borrowerName || "Unknown Borrower",
            };
          });
        } else {
          const loanObjects = recordsArray
            .map((record) => {
              let loanObject = null;

              if (record.fullRecord) {
                loanObject = record.fullRecord;
              } else if (record.loan) {
                loanObject = record.loan;
              } else if (record.metadata) {
                loanObject = record;
              } else if (record.pid || record.id) {
                loanObject = {
                  pid: record.pid || record.id,
                  metadata: {
                    pid: record.pid || record.id,
                    ...record.metadata,
                  },
                };
              } else {
                const loanPid =
                  record.loanId ||
                  record.loan_pid ||
                  record.loan?.pid ||
                  record.loan?.metadata?.pid;
                if (loanPid) {
                  loanObject = {
                    pid: loanPid,
                    metadata: { pid: loanPid },
                  };
                }
              }

              return loanObject;
            })
            .filter((loan) => loan && (loan.pid || loan.metadata?.pid));

          if (loanObjects.length > 0) {
            await preloadBorrowerInfo(loanObjects);
          }

          enriched = recordsArray.map((record, index) => {
            const loanObject = record.fullRecord ||
              record.loan || {
                pid: record.loanId || record.id,
                metadata: { pid: record.loanId || record.id },
              };

            const borrowerInfo = getFullBorrowerInfoSync(loanObject);

            return {
              ...record,
              borrowerName: borrowerInfo.displayName,
              borrowerProgram: borrowerInfo.program,
              borrowerProgramCode: borrowerInfo.programCode,
              borrowerYear: borrowerInfo.year,
              borrowerCollege: borrowerInfo.college,
              borrowerSemester: borrowerInfo.semester,
              displayProgram: borrowerInfo.displayProgram,
              programYear: borrowerInfo.programYear,
            };
          });
        }

        setEnrichedRecords(enriched);
      } catch (error) {
        const fallbackRecords = Array.isArray(loanRecords) ? loanRecords : [];
        setEnrichedRecords(fallbackRecords);
      } finally {
        setIsLoading(false);
      }
    };

    enrichLoanRecords();
  }, [loanRecords]);

  const renderEmptyRows = () => {
    const emptyRowsNeeded = Math.max(
      0,
      rowsPerPage - currentPageRecords.length
    );
    return [...Array(emptyRowsNeeded)].map((_, index) => (
      <tr key={`empty-${index}`}>
        <td style={STYLES.emptyCell}>&nbsp;</td>
        <td style={STYLES.emptyCell}>&nbsp;</td>
        <td style={STYLES.emptyCell}>&nbsp;</td>
        <td style={STYLES.emptyCell}>&nbsp;</td>
      </tr>
    ));
  };

  const renderProgramYear = (record) => {
    if (isLoading) {
      return "Loading...";
    }

    return record.programYear || "N/A";
  };

  return (
    <div style={STYLES.tableContainer}>
      <table style={STYLES.table}>
        <thead>
          <tr>
            <th style={{ ...STYLES.headerCell, width: "20%" }}>
              Date Borrowed
            </th>
            <th style={{ ...STYLES.headerCell, width: "30%" }}>
              Borrower/s Name
            </th>
            <th style={{ ...STYLES.headerCell, width: "30%" }}>
              Year & Program
            </th>
            <th style={{ ...STYLES.headerCell, width: "20%" }}>
              Date Returned
            </th>
          </tr>
        </thead>
        <tbody>
          {currentPageRecords.map((record, index) => (
            <tr key={index}>
              <td style={STYLES.dataCell}>{record.checkOutDate}</td>
              <td style={STYLES.dataCell}>
                {isLoading ? "Loading..." : record.borrowerName}
              </td>
              <td style={isLoading ? STYLES.loadingCell : STYLES.dataCell}>
                {renderProgramYear(record)}
              </td>
              <td style={STYLES.dataCell}>{record.returnDate}</td>
            </tr>
          ))}
          {renderEmptyRows()}
        </tbody>
      </table>
    </div>
  );
};

LoanTable.propTypes = {
  loanRecords: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        checkOutDate: PropTypes.string,
        borrowerName: PropTypes.string,
        returnDate: PropTypes.string,
        programYear: PropTypes.string,
        loanId: PropTypes.string,
        id: PropTypes.string,
        fullRecord: PropTypes.object,
        loan: PropTypes.object,
      })
    ),
    PropTypes.object,
  ]).isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number,
  isBackCard: PropTypes.bool,
};

export default LoanTable;
